import { clsx } from "clsx";
import {
  Children,
  FC,
  Fragment,
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Button } from "../Button/Button";
import { DropdownMenu } from "../DropdownMenu/DropdownMenu";
import type { IconSlot } from "../Icons/Icons";
import { Placement, Tooltip } from "../Tooltip/Tooltip";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./Card.css";
import { Icon } from "../Icons";

/**
 * Configuration for an action button in the Card header
 */
export interface CardActionButtonConfig {
  /**
   * Icon to display in the action button
   */
  icon?: IconSlot;

  /**
   * Label text for the action button
   */
  label?: string;

  /**
   * Click handler for the action button
   */
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;

  /**
   * Whether the action button should be disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Tooltip content for the action button
   */
  tooltip?: string;

  /**
   * Placement of the tooltip for the action button
   */
  tooltipPlacement?: Placement;
}

/**
 * Props for the Card component
 */
export interface CardProps {
  /**
   * Title displayed in the card header
   * If not provided, no header will be rendered
   */
  title?: ReactNode;

  /**
   * Optional icon rendered before the title in the card header.
   * Typically an `<Icon name="..." />` element. Hidden when `title` is not set.
   */
  titleIcon?: ReactNode;

  /**
   * When true, the title (and `titleIcon`) is rendered *outside* the card's
   * background / border, sitting directly above the card body. Useful for
   * section-style layouts where the heading should not appear to belong to
   * the visual container. `actionButtons`, `status` and `headerActions` are
   * ignored in this mode to keep the external label clean.
   * @default false
   */
  titleOutside?: boolean;

  /**
   * Optional footnote text to display next to the title
   * Rendered in gray with Body size="sm"
   */
  footnote?: string;

  /**
   * Content to display in the card body
   */
  children?: ReactNode;

  /**
   * Size variant of the card
   * - "small": Compact padding, smaller title and buttons
   * - "medium": Standard padding, standard title and buttons
   * @default "medium"
   */
  size?: "small" | "medium";

  /**
   * Array of action button configurations to display in the header
   * Only displayed if title is provided
   */
  actionButtons?: CardActionButtonConfig[];

  /**
   * When true, `actionButtons` collapse into a single three-dot (⋯) overflow
   * menu instead of rendering inline. Use on narrow containers, where a row of
   * labelled buttons would overlap the title or spill out of the card.
   * @default false
   */
  actionsAsMenu?: boolean;

  /**
   * Custom CSS class string to apply to the card container
   */
  className?: string;

  /**
   * Custom CSS class string to apply to the card header
   */
  headerClassName?: string;

  /**
   * Custom CSS class string to apply to the card content
   */
  contentClassName?: string;

  /**
   * Optional status indicator displayed next to the title
   * Can be interactive with dropdown options for status selection
   */
  status?: {
    /** Current status title text */
    title: string;
    /** Icon to display for the current status */
    icon: ReactNode;
    /**
     * Whether the status is rendered on a small screen layout.
     * When true, the label is hidden and shown via tooltip (parent should pass the value).
     */
    isSmallScreen?: boolean;
    /** Optional array of status options for dropdown selection */
    options?: Array<{
      /** Icon for this status option */
      icon: IconSlot;
      /** Label text for this status option */
      label: string;
      /** Value identifier for this status option */
      value: string;
    }>;
    /** Callback fired when a status option is selected from the dropdown */
    onChange?: (value: string) => void;
  };

  /**
   * Placement for the status indicator in the header.
   * - "inline": status renders next to the title (default)
   * - "opposite": status renders opposite the title, alongside action buttons
   */
  statusPlacement?: "inline" | "opposite";

  /**
   * Optional click handler that makes the entire card clickable
   */
  onClick?: MouseEventHandler<HTMLDivElement>;

  /**
   * Whether the card should present clickable affordances when onClick is set.
   * Defaults to true when onClick is provided.
   */
  isInteractive?: boolean;

  /**
   * Custom action elements to render in the header alongside the action buttons.
   * Useful for complex components like DropdownButton that don't fit the
   * standard CardActionButtonConfig pattern.
   */
  headerActions?: ReactNode;

  /**
   * Custom elements to render at the LEFT of the action-button group in the
   * header (i.e. before any `actionButtons`). Use this when you need a
   * stateful trigger (e.g. an ingestion-progress pill that opens a modal)
   * to sit ahead of the standard expand/collapse/export buttons.
   */
  leadingHeaderActions?: ReactNode;

  /**
   * Optional subtitle displayed below the header (always visible, even when collapsed).
   */
  subtitle?: ReactNode;

  /**
   * Whether the card body can be collapsed by clicking the chevron in the header.
   * Only has an effect when the card has children.
   * @default false
   */
  collapsible?: boolean;

  /**
   * Whether the card starts in the collapsed state.
   * @default false
   */
  defaultCollapsed?: boolean;

  /**
   * Optional extra section rendered after children that can be collapsed.
   * When provided, the chevron controls only this section; regular children
   * remain always visible. Use this instead of `collapsible` when you need
   * the card body to stay visible but want a bottom section to toggle.
   */
  collapsibleContent?: ReactNode;

  /**
   * When set, the card content is initially clamped to this many lines with
   * an ellipsis ("..."). Clicking anywhere on the card toggles between
   * the clamped preview and the full content with a smooth height animation.
   * If the content fits within the line limit, no clamping or interaction is applied.
   *
   * **Mutually exclusive** with `collapsible` / `collapsibleContent`.
   * Do not combine — both manipulate content height and will conflict.
   */
  collapsedLines?: number;
}

/**
 * Card Component
 *
 * A flexible card container component for displaying content with optional
 * title and action buttons. Provides consistent styling across the application
 * with two size variants.
 *
 * Features:
 * - Two size variants: small (compact) and medium (standard)
 * - Optional header with title and action buttons
 * - Configurable padding and spacing
 * - Design system integration with CSS custom properties
 * - Consistent styling with other Indigo components
 *
 * @example
 * // Basic card without title
 * <Card>
 *   <p>Card content goes here</p>
 * </Card>
 *
 * @example
 * // Card with title
 * <Card title="User Information">
 *   <UserDetails />
 * </Card>
 *
 * @example
 * // Small card with title and action button
 * <Card
 *   title="Quick Stats"
 *   size="small"
 *   actionButtons={[
 *     {
 *       icon: <Icon name="copy" />,
 *       label: "Copy",
 *       onClick: handleCopy
 *     }
 *   ]}
 * >
 *   <StatsContent />
 * </Card>
 *
 * @example
 * // Medium card with multiple action buttons
 * <Card
 *   title="Document Details"
 *   size="medium"
 *   actionButtons={[
 *     {
 *       icon: <Icon name="download" />,
 *       label: "Download",
 *       onClick: handleDownload
 *     },
 *     {
 *       icon: <Icon name="share" />,
 *       label: "Share",
 *       onClick: handleShare
 *     }
 *   ]}
 * >
 *   <DocumentContent />
 * </Card>
 */
export const Card: FC<CardProps> = ({
  title,
  titleIcon,
  titleOutside = false,
  footnote,
  children,
  size = "medium",
  actionButtons,
  actionsAsMenu = false,
  className,
  headerClassName = "",
  contentClassName = "",
  status,
  statusPlacement = "inline",
  onClick,
  isInteractive,
  headerActions,
  leadingHeaderActions,
  collapsible = false,
  defaultCollapsed = false,
  subtitle,
  collapsibleContent,
  collapsedLines,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const contentRef = useRef<HTMLDivElement>(null);
  const collapsibleContentRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const prevModeRef = useRef<boolean | undefined>(undefined);

  // --- collapsedLines mode ---
  const isLineClampMode = collapsedLines !== undefined && collapsedLines > 0;

  if (
    process.env.NODE_ENV !== "production" &&
    isLineClampMode &&
    (collapsible || collapsibleContent)
  ) {
    console.warn(
      "Card: `collapsedLines` is mutually exclusive with `collapsible`/`collapsibleContent`. Do not combine.",
    );
  }
  const [lineClampOverflows, setLineClampOverflows] = useState(false);
  const [lineClampExpanded, setLineClampExpanded] = useState(false);
  const lineClampHeights = useRef<{ clamped: number; full: number }>({
    clamped: 0,
    full: 0,
  });
  const lineClampAnimating = useRef(false);

  // Measure whether content overflows the line clamp.
  // Re-measure whenever children change (content loads async).
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useLayoutEffect(() => {
    if (!isLineClampMode || lineClampAnimating.current) return;
    const el = contentRef.current;
    if (!el) return;

    const savedCss = el.style.cssText;
    const hadClampClass = el.classList.contains(
      "indigo-card__content--line-clamped",
    );

    // Measure full height
    el.classList.remove("indigo-card__content--line-clamped");
    el.style.height = "auto";
    const fullHeight = el.scrollHeight;

    // Measure clamped height
    el.classList.add("indigo-card__content--line-clamped");
    el.style.height = "";
    const clampedHeight = el.clientHeight;

    lineClampHeights.current = { clamped: clampedHeight, full: fullHeight };
    setLineClampOverflows(fullHeight > clampedHeight + 1);

    // Restore
    el.style.cssText = savedCss;
    if (hadClampClass && !lineClampExpanded) {
      el.classList.add("indigo-card__content--line-clamped");
    } else {
      el.classList.remove("indigo-card__content--line-clamped");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLineClampMode, lineClampExpanded, children]);

  const toggleLineClamp = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const heights = lineClampHeights.current;

    const isClamped = el.classList.contains(
      "indigo-card__content--line-clamped",
    );

    lineClampAnimating.current = true;

    if (isClamped) {
      const from = heights.clamped;
      const to = heights.full;
      el.classList.remove("indigo-card__content--line-clamped");
      el.style.height = `${from}px`;
      void el.offsetHeight;
      el.style.height = `${to}px`;
      el.addEventListener(
        "transitionend",
        () => {
          el.style.height = "";
          lineClampAnimating.current = false;
        },
        { once: true },
      );
    } else {
      const from = el.scrollHeight;
      const to = heights.clamped;
      el.style.height = `${from}px`;
      void el.offsetHeight;
      el.style.height = `${to}px`;
      el.addEventListener(
        "transitionend",
        () => {
          el.style.height = "";
          el.classList.add("indigo-card__content--line-clamped");
          lineClampAnimating.current = false;
        },
        { once: true },
      );
    }

    setLineClampExpanded((prev) => !prev);
  }, []);

  // When collapsibleContent is provided the extra section collapses; otherwise
  // the main content div collapses (legacy collapsible prop behaviour).
  const isCollapsibleContentMode = collapsibleContent !== undefined;
  const isAnimated = collapsible || isCollapsibleContentMode;

  useLayoutEffect(() => {
    const node = isCollapsibleContentMode
      ? collapsibleContentRef.current
      : contentRef.current;

    // Track mode changes even when animation is disabled
    const modeChanged =
      prevModeRef.current !== undefined &&
      prevModeRef.current !== isCollapsibleContentMode;
    prevModeRef.current = isCollapsibleContentMode;

    if (!isAnimated || !node) return;

    // On mount or when collapsible mode switches, set state without animation
    if (isFirstRender.current || modeChanged) {
      isFirstRender.current = false;
      node.style.transition = "none";
      node.style.height = isCollapsed ? "0" : "auto";
      node.style.opacity = isCollapsed ? "0" : "1";
      void node.offsetHeight;
      node.style.transition = "";
      return;
    }

    const handleTransitionEnd = () => {
      if (!isCollapsed && node) {
        node.style.height = "auto";
      }
    };
    node.addEventListener("transitionend", handleTransitionEnd);

    if (isCollapsed) {
      const height = node.offsetHeight;
      node.style.height = `${height}px`;
      void node.offsetHeight;
      node.style.height = "0";
      node.style.opacity = "0";
    } else {
      node.style.height = "auto";
      node.style.opacity = "1";
      const height = node.offsetHeight;
      node.style.height = "0";
      void node.offsetHeight;
      node.style.height = `${height}px`;
      node.style.opacity = "1";
    }

    return () => node.removeEventListener("transitionend", handleTransitionEnd);
  }, [isCollapsed, isAnimated, isCollapsibleContentMode]);

  const toggleCollapse = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!isAnimated) return;
    const node = isCollapsibleContentMode
      ? collapsibleContentRef.current
      : contentRef.current;
    if (node && !isCollapsed) {
      const height = node.offsetHeight;
      node.style.height = `${height}px`;
      void node.offsetHeight;
    }
    requestAnimationFrame(() => setIsCollapsed((prev) => !prev));
  };
  /**
   * Renders all action buttons in the header using Indigo Button component
   */
  const renderActionButtons = () => {
    const hasButtons = actionButtons && actionButtons.length > 0;
    if (!hasButtons && !leadingHeaderActions) return null;

    // Map size to Button size prop
    const buttonSize = size === "small" ? "sm" : "md";

    if (actionsAsMenu && hasButtons) {
      return (
        <div className="indigo-card__actions">
          {leadingHeaderActions}
          <DropdownMenu
            buttonIcon="more"
            items={actionButtons.map((buttonConfig) => ({
              label: buttonConfig.label ?? buttonConfig.tooltip ?? "",
              icon: buttonConfig.icon,
              disabled: buttonConfig.disabled,
              tooltip: buttonConfig.tooltip,
              // Menu items carry no click event. Invoke the handler without
              // one so a handler that wrongly reads the event fails loudly
              // rather than receiving a misleading empty object.
              onClick: () =>
                (
                  buttonConfig.onClick as (
                    e?: React.MouseEvent<HTMLButtonElement>,
                  ) => void
                )(),
            }))}
            // Right-aligned with the trigger (which sits at the header's right
            // edge) so the menu opens inward; "bottom-right" would anchor its
            // left edge to the button and run off the card.
            dropDirection="bottom-left"
            onHoverOpen={false}
          />
        </div>
      );
    }

    return (
      <div className="indigo-card__actions">
        {leadingHeaderActions}
        {(actionButtons ?? []).map((buttonConfig, index) => {
          const button = (
            <Button
              variant="ghost"
              size={buttonSize}
              onClick={buttonConfig.onClick}
              disabled={buttonConfig.disabled}
              icon={buttonConfig.icon}
              aria-label={buttonConfig.label || buttonConfig.tooltip}
            >
              {buttonConfig.label}
            </Button>
          );

          if (!buttonConfig.tooltip) {
            return <Fragment key={index}>{button}</Fragment>;
          }

          return (
            <Tooltip
              key={index}
              content={buttonConfig.tooltip}
              placement={buttonConfig.tooltipPlacement || "bottom"}
            >
              {button}
            </Tooltip>
          );
        })}
      </div>
    );
  };

  // Map size to Heading level
  const headingLevel = size === "small" ? 4 : 2;
  const hasContent = Children.count(children) > 0;
  const showChevron = (collapsible && hasContent) || isCollapsibleContentMode;

  const statusDisplayNode = status
    ? (() => {
        const shouldHideStatusTitle = Boolean(status.isSmallScreen);
        const baseDisplay = (
          <StatusDisplay
            icon={status.icon}
            title={status.title}
            hideTitle={shouldHideStatusTitle}
          />
        );

        return shouldHideStatusTitle ? (
          <Tooltip content={status.title} placement="top">
            {baseDisplay}
          </Tooltip>
        ) : (
          baseDisplay
        );
      })()
    : null;

  const statusNode = status ? (
    status.options && status.onChange ? (
      <DropdownMenu
        items={status.options.map((option) => ({
          icon: option.icon,
          label: option.label,
          onClick: () => status.onChange!(option.value),
        }))}
        buttonIcon={statusDisplayNode}
        contentSize="sm"
        dropDirection="bottom-right"
        onHoverOpen={false}
        buttonClassName="indigo-card-status-dropdown-button"
      />
    ) : (
      statusDisplayNode
    )
  ) : null;

  const actionButtonsNode = renderActionButtons();

  const rightHeaderContent =
    statusPlacement === "opposite" ? (
      statusNode || actionButtonsNode || headerActions ? (
        <div className="indigo-card__header-actions">
          {statusNode}
          {actionButtonsNode}
          {headerActions}
        </div>
      ) : null
    ) : actionButtonsNode || headerActions ? (
      <div className="indigo-card__header-actions">
        {actionButtonsNode}
        {headerActions}
      </div>
    ) : null;

  const interactive = onClick ? (isInteractive ?? true) : false;
  const lineClampInteractive = isLineClampMode && lineClampOverflows;

  const handleCardClick = (e: MouseEvent<HTMLDivElement>) => {
    if (lineClampInteractive) {
      toggleLineClamp();
    }
    onClick?.(e);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      if (lineClampInteractive) {
        event.preventDefault();
        toggleLineClamp();
      }
      if (interactive && onClick) {
        event.preventDefault();
        onClick(event as unknown as MouseEvent<HTMLDivElement>);
      }
    }
  };

  return (
    <div
      className={clsx(titleOutside && title && "indigo-card__external-wrapper")}
    >
      {titleOutside && title && (
        <div className="indigo-card__external-title">
          {titleIcon && (
            <span className="indigo-card__title-icon" aria-hidden="true">
              {titleIcon}
            </span>
          )}
          <Heading level={headingLevel} className="indigo-card__title">
            {title}
          </Heading>
        </div>
      )}
      <div
        className={clsx(
          "indigo-card",
          `indigo-card--${size}`,
          (interactive || lineClampInteractive) && "indigo-card--interactive",
          className,
        )}
        role={interactive || lineClampInteractive ? "button" : undefined}
        tabIndex={interactive || lineClampInteractive ? 0 : undefined}
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
      >
        {title && !titleOutside && (
          <div
            className={clsx(
              "indigo-card__header",
              headerClassName,
              // Drop the header's bottom margin when there's nothing visible
              // below it: no body content, or a collapsible card whose body is
              // currently collapsed (a subtitle / collapsibleContent stays
              // visible, so keep the margin in those modes).
              (!hasContent || (collapsible && isCollapsed)) &&
                !subtitle &&
                !collapsibleContent &&
                "indigo-card__header--flush",
              statusPlacement === "opposite" &&
                "indigo-card__header--status-opposite",
            )}
          >
            <div className="indigo-card__title-container">
              <div className="flex items-center gap-1">
                {showChevron && (
                  <button
                    type="button"
                    className="indigo-card__collapse-btn"
                    onClick={toggleCollapse}
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                  >
                    <Icon
                      name="caret-right"
                      sizeX={14}
                      sizeY={14}
                      className={clsx(
                        "indigo-card__collapse-icon",
                        !isCollapsed && "rotate-90",
                      )}
                    />
                  </button>
                )}
                <div className="flex items-center gap-2">
                  {titleIcon && (
                    <span
                      className="indigo-card__title-icon"
                      aria-hidden="true"
                    >
                      {titleIcon}
                    </span>
                  )}
                  <Heading level={headingLevel} className="indigo-card__title">
                    {title}
                  </Heading>
                  {statusPlacement === "inline" && statusNode}
                </div>
              </div>
              {footnote && (
                <Body size="sm" className="indigo-card__footnote">
                  {footnote}
                </Body>
              )}
            </div>
            {rightHeaderContent}
          </div>
        )}
        {subtitle && <div className="indigo-card__subtitle">{subtitle}</div>}
        <div
          ref={contentRef}
          className={clsx(
            "indigo-card__content",
            contentClassName,
            collapsible &&
              !isCollapsibleContentMode &&
              "indigo-card__content--collapsible",
            isLineClampMode && "indigo-card__content--line-clamp-animated",
            isLineClampMode &&
              lineClampOverflows &&
              !lineClampExpanded &&
              "indigo-card__content--line-clamped",
          )}
          style={
            isLineClampMode
              ? ({
                  "--indigo-card-collapsed-lines": collapsedLines,
                } as React.CSSProperties)
              : undefined
          }
        >
          {children}
        </div>
        {isCollapsibleContentMode && (
          <div
            ref={collapsibleContentRef}
            className="indigo-card__collapsible-extra"
          >
            {collapsibleContent}
          </div>
        )}
      </div>
    </div>
  );
};

interface StatusDisplayProps {
  icon: ReactNode;
  title: string;
  hideTitle?: boolean;
}

const StatusDisplay = forwardRef<HTMLDivElement, StatusDisplayProps>(
  ({ icon, title, hideTitle = false }, ref) => {
    return (
      <div
        ref={ref}
        className="indigo-card-status"
        aria-label={hideTitle ? title : undefined}
      >
        <span className="indigo-card-status-icon">{icon}</span>
        {!hideTitle && (
          <span className="indigo-card-status-title">{title}</span>
        )}
      </div>
    );
  },
);

StatusDisplay.displayName = "StatusDisplay";

Card.displayName = "Card";

export default Card;
